import React, { useState } from 'react';
import { Tabs, Collapse, Typography, Button, Space, Alert } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { message } from 'antd';

const { Text, Paragraph } = Typography;

export type ScriptLine = { speaker: string; text: string };
export type ScriptBlock = {
  phase: string;
  title: string;
  note?: string;
  lines: ScriptLine[];
};
export type ScriptTabDef = {
  id?: string;
  key: string;
  label: string;
  alert?: { type: 'info' | 'warning' | 'error' | 'success'; message: string };
  blocks: ScriptBlock[];
};

async function copyLine(text: string) {
  const t = text.trim();
  try {
    await navigator.clipboard.writeText(t);
    message.success('Copied');
  } catch {
    message.error('Copy failed');
  }
}

function ScriptLineRow({ line }: { line: ScriptLine }) {
  return (
    <div
      style={{
        background: '#fafafa',
        borderLeft: '3px solid #01696f',
        borderRadius: 4,
        padding: '12px 16px',
        marginBottom: 12,
        position: 'relative',
      }}
    >
      <Text strong style={{ fontSize: 11, textTransform: 'uppercase', color: '#01696f', display: 'block', marginBottom: 6 }}>
        {line.speaker}
      </Text>
      <Paragraph style={{ marginBottom: 0, paddingRight: 72 }}>{line.text}</Paragraph>
      <Button
        type="text"
        size="small"
        icon={<CopyOutlined />}
        style={{ position: 'absolute', top: 8, right: 8 }}
        onClick={() => copyLine(line.text)}
      >
        Copy
      </Button>
    </div>
  );
}

export function ScriptLibrary({ tabs, title }: { tabs: ScriptTabDef[]; title: string }) {
  const [active, setActive] = useState(tabs[0]?.key || '');

  return (
    <div className="retain-iq-scripts">
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        {title}
      </Typography.Title>
      <Tabs activeKey={active} onChange={setActive} items={tabs.map((tab) => ({
        key: tab.key,
        label: tab.label,
        children: (
          <div>
            {tab.alert && <Alert style={{ marginBottom: 16 }} type={tab.alert.type} message={tab.alert.message} showIcon />}
            <Collapse
              defaultActiveKey={tab.blocks.length ? ['0'] : []}
              items={tab.blocks.map((block, idx) => ({
                key: String(idx),
                label: (
                  <Space>
                    <Text code style={{ fontSize: 11 }}>{block.phase}</Text>
                    <Text strong>{block.title}</Text>
                  </Space>
                ),
                children: (
                  <div>
                    {block.note && (
                      <Alert type="info" message={block.note} style={{ marginBottom: 12 }} />
                    )}
                    {block.lines.map((line, i) => (
                      <ScriptLineRow key={i} line={line} />
                    ))}
                  </div>
                ),
              }))}
            />
          </div>
        ),
      }))} />
    </div>
  );
}
